import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DrawerService } from '../services/drawer.service';

/**
 * Keeps the page when an open drawer holds unsaved work the user chooses to keep (the email editor's design, for
 * one). Every navigation closes all drawers, so before it happens the drawers are asked, and they ask the user.
 *
 * Used on a layout route both ways: as canActivateChild for navigation within the layout (links, the browser's
 * Back and Forward) and as canDeactivate for leaving the layout.
 */
@Injectable({
  providedIn: 'root'
})
export class UnsavedDrawerContentGuard {

  // The answer for the navigation under way. The router runs a parent's canActivateChild once for every child route
  // it activates, and the user must be asked once per navigation, not once per route.
  private _answer: { navigationId: number; leave: Promise<boolean> } | null = null;

  constructor(private readonly _drawerService: DrawerService,
    private readonly _router: Router) { }

  canActivateChild(): Promise<boolean> {
    return this.askOncePerNavigation();
  }

  canDeactivate(): Promise<boolean> {
    return this.askOncePerNavigation();
  }

  private askOncePerNavigation(): Promise<boolean> {
    const navigationId = this._router.currentNavigation()?.id;
    // The router runs guards only during a navigation, but without one there is nothing to share an answer with,
    // and an answer kept under a made-up id would keep the page on later calls without asking.
    if (navigationId === undefined) {
      return this._drawerService.canCloseAll();
    }
    if (this._answer?.navigationId !== navigationId) {
      this._answer = { navigationId, leave: this._drawerService.canCloseAll() };
    }
    return this._answer.leave;
  }
}
